/**
 * Единые тексты и правила UX для обрыва/переподключения Colyseus (хаб, онлайн-лобби, стол).
 * Коды ошибок согласованы с server/src/contracts/messages.js (ERROR_CODES).
 */

/** Фаза хаб-сокета в App (глобальное лобби Colyseus). */
export const HUB_SOCKET_PHASE = {
  IDLE: 'idle',
  CONNECTING: 'connecting',
  ONLINE: 'online',
  RECONNECTING: 'reconnecting',
  OFFLINE: 'offline',
};

/**
 * @param {string} phase
 * @returns {'online'|'reconnecting'|'offline'|undefined}
 */
export function hubPhaseToHeaderConnectionStatus(phase) {
  if (phase === HUB_SOCKET_PHASE.ONLINE) return 'online';
  if (phase === HUB_SOCKET_PHASE.RECONNECTING || phase === HUB_SOCKET_PHASE.CONNECTING) return 'reconnecting';
  if (phase === HUB_SOCKET_PHASE.OFFLINE) return 'offline';
  return undefined;
}

/**
 * Сообщение при закрытии старой сессии из‑за второго клиента с тем же userId.
 * @param {'lobby'|'table'|'room'} context
 */
export function duplicateSessionUserMessage(context) {
  if (context === 'table') {
    return 'Сессия открыта на другом устройстве. Зайдите за стол снова из лобби.';
  }
  if (context === 'room') {
    return 'Сессия открыта на другом устройстве. Зайдите в комнату снова из лобби.';
  }
  return 'Лобби открыто на другом устройстве.';
}

/** Постоянная строка под полем ошибки (GameTable и т.п.). */
export function duplicateSessionSetErrorMessage(context) {
  if (context === 'table') {
    return 'Сессия открыта на другом устройстве. Вернитесь в лобби и откройте стол снова.';
  }
  if (context === 'room') {
    return 'Сессия открыта на другом устройстве. Вернитесь в лобби и откройте комнату снова.';
  }
  return 'Сессия открыта на другом устройстве. Вернитесь в лобби.';
}

/**
 * @param {'lobby'|'table'|'room'} context
 * @param {unknown} payloadError — payload.error с финального disconnect
 */
export function finalDisconnectUserMessage(context, payloadError) {
  const hint = formatPayloadErrorHint(payloadError);
  if (context === 'table') {
    return hint ? `Связь со столом потеряна: ${hint}` : 'Связь со столом потеряна.';
  }
  if (context === 'room') {
    return hint ? `Связь с комнатой потеряна: ${hint}` : 'Связь с комнатой потеряна.';
  }
  return hint ? `Связь с лобби потеряна: ${hint}` : 'Связь с лобби потеряна.';
}

/** Подсказка под стол/комнату при обрыве (без префикса). */
export function finalDisconnectInlineErrorText(context) {
  if (context === 'table') {
    return 'Связь со столом потеряна. Вернитесь в лобби и откройте стол снова.';
  }
  if (context === 'room') {
    return 'Связь с комнатой потеряна. Вернитесь в лобби и откройте снова.';
  }
  return '';
}

/**
 * @param {unknown} payload
 * @returns {boolean} true если использовался fallback joinOrCreate после reconnect token
 */
export function isReconnectFallbackJoin(payload) {
  return Boolean(payload && typeof payload === 'object' && payload.fallback === 'join');
}

export function reconnectSuccessUserMessage({ context, fallbackJoin }) {
  if (context === 'lobby') {
    return fallbackJoin ? 'Лобби: переподключение выполнено.' : 'Соединение с лобби восстановлено.';
  }
  if (context === 'table') {
    return fallbackJoin ? 'Переподключение к столу выполнено.' : 'Соединение со столом восстановлено.';
  }
  return fallbackJoin ? 'Переподключение к комнате выполнено.' : 'Соединение восстановлено.';
}

function formatPayloadErrorHint(payloadError) {
  if (payloadError && typeof payloadError === 'object' && typeof payloadError.message === 'string') {
    const m = payloadError.message.trim();
    return m || '';
  }
  if (typeof payloadError === 'string') {
    return payloadError.trim();
  }
  return '';
}

/** Коды system/error, которые не должны давать «красный» баннер блока столов / шум в хабе. */
export const LOBBY_TABLES_ERROR_CODES_SUPPRESSED = new Set([
  'BAD_PAYLOAD',
  'RATE_LIMITED',
  'TABLE_NOT_FOUND',
  'TABLE_BAD_PASSWORD',
  'JOIN_TIMEOUT',
  'OUT_OF_TURN',
  'INVALID_STATE',
  'INSUFFICIENT_FUNDS',
  'DUPLICATE_COMMAND',
  'INVITE_ACCESS_DENIED',
]);

/**
 * @param {unknown} payload — событие error с лобби-транспорта
 */
export function shouldShowLobbyTablesSocketErrorBanner(payload) {
  const code = String(payload?.code || '').trim();
  if (!code) return false;
  return !LOBBY_TABLES_ERROR_CODES_SUPPRESSED.has(code);
}

/**
 * Ошибки игрового транспорта, не связанные с обрывом связи (не тостить как «ошибка игры»).
 * @param {unknown} payload
 */
export function isGameTransportErrorNoise(payload) {
  const code = String(payload?.code || '').trim();
  if (!code) return false;
  return (
    code === 'RATE_LIMITED' ||
    code === 'BAD_PAYLOAD' ||
    code === 'DUPLICATE_COMMAND' ||
    code === 'OUT_OF_TURN' ||
    code === 'INVALID_STATE' ||
    code === 'TABLE_NOT_FOUND' ||
    code === 'TABLE_BAD_PASSWORD'
  );
}

/**
 * Серверные коды для единообразного маппинга (дублирует server/src/contracts/messages.js ERROR_CODES).
 */
export const SERVER_ERROR_CODE = {
  BAD_PAYLOAD: 'BAD_PAYLOAD',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  RATE_LIMITED: 'RATE_LIMITED',
};
